import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, AlertTriangle, CheckCircle, DollarSign, Scale, FileText, Building2, User, CalendarIcon } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import { apiClient } from "@/services/api/client";
import { PROCESS_STATUS, getProcessStatusOptions } from "@/constants/process-status";

// TODO: Backend needs Modality endpoint
interface Modality {
  id: number;
  name: string;
}

interface ProcessFormValues {
  code: string;
  description: string;
  modality_id: number | string;
  object: string;
  estimated_value: string;
  publication_date: Date | null;
  opening_date: Date | null;
  opening_time: string;
  responsible: string;
  status: string;
}

interface ProcessFormV3Props {
  initialValues?: Partial<ProcessFormValues>;
  onSubmit: (values: ProcessFormValues) => Promise<void>;
  isEditing?: boolean;
  submitButtonText?: string;
}



export default function ProcessFormV3({
  initialValues,
  onSubmit,
  isEditing = false,
  submitButtonText = "Criar Processo",
}: ProcessFormV3Props) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [legalAlerts, setLegalAlerts] = useState<
    Array<{ type: string; message: string }>
  >([]);

  const [formValues, setFormValues] = useState<ProcessFormValues>({
    code: initialValues?.code || "",
    description: initialValues?.description || "",
    modality_id: initialValues?.modality_id || 0,
    object: initialValues?.object || "",
    estimated_value: initialValues?.estimated_value || "",
    publication_date: initialValues?.publication_date || null,
    opening_date: initialValues?.opening_date || null,
    opening_time: initialValues?.opening_time || "",
    responsible: initialValues?.responsible || "",
    status: initialValues?.status || PROCESS_STATUS.ABERTO,
  });

  // TODO: Backend needs modalities endpoint
  const { data: modalities = [] } = useQuery<Modality[]>({
    queryKey: ["modalities"],
    queryFn: async () => {
      try {
        const response = await apiClient.get("/modalities/");
        return Array.isArray(response.data)
          ? response.data
          : response.data.results || [];
      } catch (error) {
        console.error("Error fetching modalities:", error);
        return [];
      }
    },
  });

  // TODO: Backend needs LegalParameter entity/endpoint
  const { data: legalParameters = [] } = useQuery({
    queryKey: ["legal-parameters"],
    queryFn: async () => {
      // Placeholder - backend needs to implement LegalParameter entity
      // Should return: [{ parameter_code: 'dispensa_obras', value: 57000, legal_reference: 'Art. 75, I, Lei 14.133/21' }]
      return [];
    },
  });

  // Format currency
  const formatCurrency = (value: string) => {
    if (!value) return "";
    const numbers = value.replace(/\D/g, "");
    const numberValue = parseFloat(numbers) / 100;
    return numberValue.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Parse currency to number
  const parseCurrency = (value: string) => {
    if (!value) return 0;
    return parseFloat(value.replace(/\./g, "").replace(",", "."));
  };

  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const formatted = formatCurrency(rawValue);
    setFormValues((prev) => ({ ...prev, estimated_value: formatted }));

    // Check legal limits
    if (formatted) {
      const numericValue = parseCurrency(formatted);
      checkLegalLimits(numericValue, formValues.modality_id);
    }
  };

  // TODO: Implement legal limits check when backend has LegalParameter
  const checkLegalLimits = (value: number, modalityId: number | string) => {
    const alerts: Array<{ type: string; message: string }> = [];

    // Find modality
    const modality = modalities.find((m) => String(m.id) === String(modalityId));
    if (!modality) {
      setLegalAlerts([]);
      return;
    }

    // Check if it's dispensa or inexigibilidade
    const modalityName = modality.name.toLowerCase();
    if (
      modalityName.includes("dispensa") ||
      modalityName.includes("inexigibilidade")
    ) {
      // TODO: Check against legal parameters when backend is ready
      if (legalParameters.length > 0) {
        const dispensaObras = legalParameters.find(
          (p: any) => p.parameter_code === "dispensa_obras"
        );
        const dispensaCompras = legalParameters.find(
          (p: any) => p.parameter_code === "dispensa_compras"
        );

        if (dispensaObras && value > dispensaObras.value) {
          alerts.push({
            type: "warning",
            message: `Valor acima do limite de dispensa para obras (R$ ${dispensaObras.value.toLocaleString(
              "pt-BR",
              { minimumFractionDigits: 2 }
            )}). ${dispensaObras.legal_reference}`,
          });
        }

        if (dispensaCompras && value > dispensaCompras.value) {
          alerts.push({
            type: "warning",
            message: `Valor acima do limite de dispensa para compras/serviços (R$ ${dispensaCompras.value.toLocaleString(
              "pt-BR",
              { minimumFractionDigits: 2 }
            )}). ${dispensaCompras.legal_reference}`,
          });
        }
      } else {
        // Placeholder alert when backend is not ready
        alerts.push({
          type: "info",
          message:
            "TODO: Verificação de limites legais pendente (backend precisa implementar LegalParameter)",
        });
      }
    }

    setLegalAlerts(alerts);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);

      // Simple validation
      if (!formValues.object || !formValues.modality_id) {
        toast({
          title: "Erro de validação",
          description: "Por favor, preencha os campos obrigatórios (Objeto e Modalidade).",
          variant: "destructive",
        });
        return;
      }

      // The estimated_value is already a string in formValues, no need to parse and re-stringify.
      // The modality_id must be sent as number
      const submitData = {
        ...formValues,
        modality_id: Number(formValues.modality_id)
      };

      await onSubmit(submitData);
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setLoading(false);
    }
  };

  const getModalityStages = () => {
    if (!formValues.modality_id) return [];
    const modality = modalities.find(m => String(m.id) === String(formValues.modality_id));
    return (modality as any)?.stages || [];
  };

  const stages = getModalityStages();

  return (
    <div className="p-6 lg:p-8 min-h-screen">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate("/processes")}
            className="border-[#1e2835] text-[#f5f5f0] hover:bg-[#1e2835]"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-[#f5f5f0] mb-2 flex items-center gap-3">
              <FileText className="w-8 h-8 text-[#2563eb]" />
              {isEditing ? "Editar Processo" : "Nova Ficha de Processo"}
            </h1>
            <p className="text-[#94a3b8]">
              {isEditing
                ? "Atualize as informações do processo licitatório"
                : "Preencha as informações essenciais para iniciar o processo licitatório"}
            </p>
          </div>
        </div>

        {/* Legal Alerts */}
        {legalAlerts.length > 0 && (
          <div className="space-y-3">
            {legalAlerts.map((alert, index) => (
              <Alert key={index} className="bg-[#f59e0b] bg-opacity-10 border-[#f59e0b] border backdrop-blur-sm">
                <AlertTriangle className="h-5 w-5 text-[#f59e0b]" />
                <AlertDescription className="text-[#f59e0b]">
                  {alert.message}
                </AlertDescription>
              </Alert>
            ))}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>

          <Card className="bg-[#151b23] bg-opacity-80 backdrop-blur-lg border-[#1e2835]">
            <CardHeader className="border-b border-[#1e2835]">
              <CardTitle className="text-[#f5f5f0]">Dados do Processo</CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              {/* Objeto (TEXTAREA MAIOR) */}
              <div className="space-y-2">
                <Label htmlFor="object" className="text-[#e2e8f0] font-medium flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#3b82f6]" />
                  Objeto da Licitação (Resumido) *
                </Label>
                <Textarea
                  id="object"
                  value={formValues.object}
                  onChange={(e) => setFormValues(prev => ({ ...prev, object: e.target.value }))}
                  placeholder="Descreva detalhadamente o objeto da licitação. Quanto mais informações você fornecer, melhor será o resultado da geração de documentos por IA. Ex: Contratação de empresa especializada para fornecimento de equipamentos de informática incluindo computadores, notebooks, impressoras e periféricos, destinados aos setores administrativos da Secretaria de Educação..."
                  required
                  rows={6}
                  className="bg-[#0a0e13] border-[#1e2835] text-[#f5f5f0] placeholder:text-[#64748b] focus:border-[#2563eb] resize-y min-h-[150px]"
                />
                <p className="text-xs text-[#94a3b8]">
                  💡 <strong className="text-[#3b82f6]">Dica:</strong> Seja o mais detalhado possível! Informações como quantidades, especificações técnicas, prazos e requisitos especiais ajudarão a IA a gerar documentos mais precisos e completos.
                </p>
              </div>

              {/* Número do Processo */}
              <div className="space-y-2">
                <Label htmlFor="code" className="text-[#e2e8f0] font-medium flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#3b82f6]" />
                  Número do Processo *
                </Label>
                <Input
                  id="code"
                  value={formValues.code}
                  onChange={(e) => setFormValues(prev => ({ ...prev, code: e.target.value }))}
                  placeholder="Ex: 001/2025"
                  required
                  className="bg-[#0a0e13] border-[#1e2835] text-[#f5f5f0] placeholder:text-[#64748b] focus:border-[#2563eb]"
                />
              </div>

              {/* Responsável */}
              <div className="space-y-2">
                <Label htmlFor="responsible" className="text-[#e2e8f0] font-medium flex items-center gap-2">
                  <User className="w-4 h-4 text-[#3b82f6]" />
                  Responsável pelo Processo *
                </Label>
                <Input
                  id="responsible"
                  value={formValues.responsible}
                  onChange={(e) => setFormValues(prev => ({ ...prev, responsible: e.target.value }))}
                  placeholder="Nome completo do responsável"
                  required
                  className="bg-[#0a0e13] border-[#1e2835] text-[#f5f5f0] placeholder:text-[#64748b] focus:border-[#2563eb]"
                />
              </div>

              {/* Modalidade e Status */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="modality" className="text-[#e2e8f0] font-medium flex items-center gap-2">
                    <Scale className="w-4 h-4 text-[#3b82f6]" />
                    Modalidade de Licitação *
                  </Label>
                  <Select
                    value={formValues.modality_id.toString()}
                    onValueChange={(value) => {
                      setFormValues(prev => ({ ...prev, modality_id: value }));
                      if (formValues.estimated_value) {
                        checkLegalLimits(parseCurrency(formValues.estimated_value), Number(value));
                      }
                    }}
                    required
                  >
                    <SelectTrigger className="bg-[#0a0e13] border-[#1e2835] text-[#f5f5f0] focus:border-[#2563eb]">
                      <SelectValue placeholder="Selecione a modalidade" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#151b23] border-[#1e2835]">
                      {modalities.map((modality) => (
                        <SelectItem key={modality.id} value={modality.id.toString()} className="text-[#f5f5f0] focus:bg-[#1e2835]">
                          {modality.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="status" className="text-[#e2e8f0] font-medium flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#3b82f6]" />
                    Status *
                  </Label>
                  <Select
                    value={formValues.status}
                    onValueChange={(value) => setFormValues(prev => ({ ...prev, status: value }))}
                    required
                  >
                    <SelectTrigger className="bg-[#0a0e13] border-[#1e2835] text-[#f5f5f0] focus:border-[#2563eb]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#151b23] border-[#1e2835]">
                      {getProcessStatusOptions().map((statusOption) => (
                        <SelectItem key={statusOption.value} value={statusOption.value} className="text-[#f5f5f0] focus:bg-[#1e2835]">
                          {statusOption.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Valor Estimado */}
              <div className="space-y-2">
                <Label htmlFor="estimated_value" className="text-[#e2e8f0] font-medium flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-[#3b82f6]" />
                  Valor Estimado (R$) *
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]">
                    R$
                  </span>
                  <Input
                    id="estimated_value"
                    value={formValues.estimated_value}
                    onChange={handleCurrencyChange}
                    placeholder="0,00"
                    required
                    className="bg-[#0a0e13] border-[#1e2835] text-[#f5f5f0] placeholder:text-[#64748b] focus:border-[#2563eb] pl-12"
                  />
                </div>
              </div>

              {/* Datas */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="publication_date" className="text-[#e2e8f0] font-medium flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-[#3b82f6]" />
                    Data de Publicação
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal bg-[#0a0e13] border-[#1e2835] text-[#f5f5f0] hover:bg-[#1e2835]",
                          !formValues.publication_date && "text-[#64748b]"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formValues.publication_date ? (
                          format(formValues.publication_date, "PPP", { locale: ptBR })
                        ) : (
                          <span>Selecione a data</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#151b23] border-[#1e2835]" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={formValues.publication_date || undefined}
                        onSelect={(date) => setFormValues(prev => ({ ...prev, publication_date: date || null }))}
                        initialFocus
                        locale={ptBR}
                        className="bg-[#151b23]"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="opening_date" className="text-[#e2e8f0] font-medium flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-[#3b82f6]" />
                    Data de Abertura
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal bg-[#0a0e13] border-[#1e2835] text-[#f5f5f0] hover:bg-[#1e2835]",
                          !formValues.opening_date && "text-[#64748b]"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formValues.opening_date ? (
                          format(formValues.opening_date, "PPP", { locale: ptBR })
                        ) : (
                          <span>Selecione a data</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#151b23] border-[#1e2835]" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={formValues.opening_date || undefined}
                        onSelect={(date) => setFormValues(prev => ({ ...prev, opening_date: date || null }))}
                        initialFocus
                        locale={ptBR}
                        className="bg-[#151b23]"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Horário de Abertura */}
              <div className="space-y-2">
                <Label htmlFor="opening_time" className="text-[#e2e8f0] font-medium">
                  Horário de Abertura
                </Label>
                <Input
                  id="opening_time"
                  type="time"
                  value={formValues.opening_time}
                  onChange={(e) => setFormValues(prev => ({ ...prev, opening_time: e.target.value }))}
                  className="bg-[#0a0e13] border-[#1e2835] text-[#f5f5f0] focus:border-[#2563eb]"
                />
              </div>

              {/* Observações */}
              <div className="space-y-2">
                <Label htmlFor="description" className="text-[#e2e8f0] font-medium">
                  Descrição Detalhada *
                </Label>
                <Textarea
                  id="description"
                  value={formValues.description}
                  onChange={(e) => setFormValues(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Informações adicionais sobre o processo..."
                  required
                  rows={4}
                  className="bg-[#0a0e13] border-[#1e2835] text-[#f5f5f0] placeholder:text-[#64748b] focus:border-[#2563eb]"
                />
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-between gap-4 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/processes")}
              className="border-[#1e2835] text-[#f5f5f0] hover:bg-[#1e2835]"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-gradient-to-r from-[#1e40af] to-[#3b82f6] hover:from-[#1e3a8a] hover:to-[#2563eb] text-white font-semibold shadow-lg"
            >
              {loading ? "Salvando..." : submitButtonText}
            </Button>
          </div>
        </form>

        {/* Info Card */}
        <Card className="bg-[#151b23] bg-opacity-50 border-[#1e3a8a] border-l-4">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-[#2563eb] flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-[#f5f5f0] mb-1">Sobre o Formulário</p>
                <p className="text-sm text-[#94a3b8]">
                  Este formulário cria a estrutura inicial do processo. Após criá-lo, você será direcionado
                  para a <strong className="text-[#3b82f6]">Timeline Visual</strong>, onde poderá gerar
                  documentos com IA ou criar manualmente. O sistema já seleciona automaticamente as etapas
                  necessárias baseado na modalidade escolhida.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
